/**
 * Parses a user's revision message for length-change intent and computes
 * a target in either words or characters (never both). Used by article/content
 * revision flows to inject numeric targets into AI prompts.
 *
 * Supports:
 * - Explicit word counts: "500 words", "1k words", "1.5k words", "1,000 words"
 * - Explicit char counts: "280 characters", "280 chars", "under 300 chars", "1k chars"
 * - Percentages: "shorter by 20%", "longer by 50%", "reduce by 30%"
 * - Keywords: "shorter", "longer", "condense", "expand", etc.
 *
 * Returns null when no length-change intent is detected.
 */

/** Which metric is driving the target — words or characters. Never both. */
export type LengthMetric = 'words' | 'chars';

export type LengthChangeIntent = {
  /** Which metric the user specified */
  metric: LengthMetric;
  /** Computed target value (in the unit specified by `metric`) */
  target: number;
  /** Whether the user wants fewer or more content */
  direction: 'shorter' | 'longer';
};

/** Persistent state for length targets across revisions. */
export type LengthTargetState = {
  /** Which metric is active, or null if no target has been set */
  metric: LengthMetric | null;
  /** The target value in the active metric's unit */
  target: number | null;
};

/** Initial state — no target set. */
export const EMPTY_LENGTH_TARGET: LengthTargetState = {
  metric: null,
  target: null,
};

/** LLMs tend to overshoot explicit word count targets — apply this discount. */
export const EXPLICIT_WORD_TARGET_DISCOUNT = 0.85;

/** LLMs undershoot character targets — overshoot by 10% to compensate. */
export const EXPLICIT_CHAR_TARGET_BOOST = 1.10;

/**
 * Builds the adjusted "Target Word Count" or "Target Character Count" instruction
 * for an AI prompt's configured target. Applies the same LLM bias multipliers
 * used in revision parsing so generation and revision paths produce consistent targets.
 *
 * Returns an empty string when neither target is set.
 */
export function buildAdjustedCountInstruction(
  targetWordCount: number | null | undefined,
  targetCharacterCount: number | null | undefined,
): string {
  if (targetWordCount) {
    return `Target Word Count: ${Math.round(targetWordCount * EXPLICIT_WORD_TARGET_DISCOUNT)}`;
  }
  if (targetCharacterCount) {
    return `Target Character Count: ${Math.round(targetCharacterCount * EXPLICIT_CHAR_TARGET_BOOST)}`;
  }
  return '';
}

/** Default multiplier for vague "shorter" requests. */
const DEFAULT_SHORTER_MULTIPLIER = 0.5;

/** Default multiplier for vague "longer" requests. */
const DEFAULT_LONGER_MULTIPLIER = 1.5;

const SHORTER_KEYWORDS = [
  'shorter',
  'reduce',
  'condense',
  'brief',
  'cut it down',
  'trim',
  'too long',
  'too wordy',
  'more concise',
  'half the length',
  'shorten',
  'less verbose',
  'tighten',
];

const LONGER_KEYWORDS = [
  'longer',
  'expand',
  'more detail',
  'too short',
  'elaborate',
  'flesh out',
  'add more',
  'go deeper',
  'lengthen',
  'more in-depth',
  'more comprehensive',
];

/**
 * Parses a revision message for an explicit word count (e.g., "500 words", "1k words").
 * Returns the parsed number or null.
 */
function parseExplicitWordCount(revision: string): number | null {
  const match = revision.match(/(\d+(?:[.,]\d+)?)\s*k?\s*words?/i);
  if (!match) return null;

  const raw = match[1].replace(',', '');
  let parsed = parseFloat(raw);

  // Handle "k" shorthand: "1k words" → 1000, "1.5k words" → 1500
  if (/\d\s*k\s*words?/i.test(revision)) {
    parsed *= 1000;
  }

  return Math.round(parsed);
}

/**
 * Parses a revision message for an explicit character count
 * (e.g., "280 characters", "280 chars", "under 300 chars", "1k characters").
 * Returns the parsed number or null.
 */
function parseExplicitCharCount(revision: string): number | null {
  const match = revision.match(
    /(?:under\s+)?(\d+(?:[.,]\d+)?)\s*k?\s*char(?:acter)?s?/i,
  );
  if (!match) return null;

  const raw = match[1].replace(',', '');
  let parsed = parseFloat(raw);

  // Handle "k" shorthand: "1k chars" → 1000
  if (/\d\s*k\s*char(?:acter)?s?/i.test(revision)) {
    parsed *= 1000;
  }

  return Math.round(parsed);
}

/**
 * Parses a revision message for a percentage change (e.g., "shorter by 20%").
 * Returns { percent, isShrink } or null.
 */
function parsePercentage(
  revision: string,
): { percent: number; isShrink: boolean } | null {
  // Match both "shorter by 20%" and "20% shorter"
  const match =
    revision.match(
      /(?:shorter|reduce|longer|expand|increase)\s+by\s+(\d+)\s*%/i,
    ) || revision.match(/(\d+)\s*%\s*(?:shorter|reduce|longer|expand|increase)/i);
  if (!match) return null;

  return {
    percent: parseInt(match[1], 10) / 100,
    isShrink: /shorter|reduce/i.test(revision),
  };
}

/**
 * Detects whether a revision message contains length-change keywords.
 */
function detectKeywordIntent(
  revision: string,
): 'shorter' | 'longer' | null {
  const lower = revision.toLowerCase();
  if (SHORTER_KEYWORDS.some(kw => lower.includes(kw))) return 'shorter';
  if (LONGER_KEYWORDS.some(kw => lower.includes(kw))) return 'longer';
  return null;
}

/**
 * Parses a user's revision message and returns the intended length change,
 * or null if no length change is detected.
 *
 * The returned intent uses exactly ONE metric (words or chars), never both.
 *
 * @param revision - The user's revision message text
 * @param currentWordCount - The current article's word count
 * @param previousTarget - The persisted target state from prior revisions
 * @param currentCharCount - The current article's character count (optional)
 */
export function parseRevisionLengthIntent(
  revision: string,
  currentWordCount: number,
  previousTarget: LengthTargetState,
  currentCharCount?: number,
): LengthChangeIntent | null {
  /** Resolve the current value in the given metric. */
  const getCurrentValue = (metric: LengthMetric): number =>
    metric === 'chars'
      ? (currentCharCount ?? currentWordCount * 5)
      : currentWordCount;

  // 1. Explicit percentage: "shorter by 20%", "20% shorter", "longer by 50%"
  //    Operates in whichever metric was previously active (defaults to words).
  const pct = parsePercentage(revision);
  if (pct) {
    const metric = previousTarget.metric ?? 'words';
    const base = previousTarget.target ?? getCurrentValue(metric);
    return {
      metric,
      target: pct.isShrink
        ? Math.round(base * (1 - pct.percent))
        : Math.round(base * (1 + pct.percent)),
      direction: pct.isShrink ? 'shorter' : 'longer',
    };
  }

  // 2. Explicit character count: "280 characters", "280 chars", "under 300 chars"
  //    Switches metric to chars.
  const explicitChars = parseExplicitCharCount(revision);
  if (explicitChars) {
    const targetChars = Math.round(explicitChars * EXPLICIT_CHAR_TARGET_BOOST);
    return {
      metric: 'chars',
      target: targetChars,
      direction: explicitChars < getCurrentValue('chars') ? 'shorter' : 'longer',
    };
  }

  // 3. Explicit word count: "500 words", "1k words"
  //    Switches metric to words.
  const explicitWords = parseExplicitWordCount(revision);
  if (explicitWords) {
    return {
      metric: 'words',
      target: Math.round(explicitWords * EXPLICIT_WORD_TARGET_DISCOUNT),
      direction: explicitWords < currentWordCount ? 'shorter' : 'longer',
    };
  }

  // 4. Keyword detection: "shorter", "longer", "condense", etc.
  //    Operates in whichever metric was previously active (defaults to words).
  const keyword = detectKeywordIntent(revision);
  if (keyword) {
    const metric = previousTarget.metric ?? 'words';
    const current = getCurrentValue(metric);
    if (keyword === 'shorter') {
      const base = previousTarget.target ?? current;
      return {
        metric,
        target: Math.round(base * DEFAULT_SHORTER_MULTIPLIER),
        direction: 'shorter',
      };
    }
    // "longer" — expand from current actual value, not stale target
    return {
      metric,
      target: Math.round(current * DEFAULT_LONGER_MULTIPLIER),
      direction: 'longer',
    };
  }

  return null;
}

/**
 * Builds the length instruction string to append to a revision prompt.
 *
 * @param intent - The parsed length change intent (from parseRevisionLengthIntent)
 * @param currentWordCount - The current article's word count
 * @param previousTarget - The persisted target state (for "maintain" instructions)
 * @param currentCharCount - The current article's character count (optional)
 */
export function buildLengthInstruction(
  intent: LengthChangeIntent | null,
  currentWordCount: number,
  previousTarget: LengthTargetState,
  currentCharCount?: number,
): string {
  const charSuffix =
    currentCharCount != null ? `, ${currentCharCount} characters` : '';
  const context = `\n\n[Current content length: ${currentWordCount} words${charSuffix}]`;

  if (intent) {
    if (intent.direction === 'shorter') {
      return intent.metric === 'chars'
        ? `${context}\nReduce to approximately ${intent.target} characters. Be concise and remove less important content.`
        : `${context}\nReduce to approximately ${intent.target} words. Remove less important sections rather than shortening every paragraph.`;
    }
    return intent.metric === 'chars'
      ? `${context}\nExpand to approximately ${intent.target} characters by adding more depth and detail.`
      : `${context}\nExpand to approximately ${intent.target} words by adding more depth, examples, and detail.`;
  }

  // No length change detected — maintain previous target if one exists
  if (previousTarget.target && previousTarget.metric) {
    const unit = previousTarget.metric === 'chars' ? 'characters' : 'words';
    return `${context}\nMaintain approximately ${previousTarget.target} ${unit}.`;
  }

  // No target ever set — just provide context
  return context;
}
